import type { CoinData, Predictions } from '../types/interfaces';

const API_BASE = 'https://api.coingecko.com/api/v3';

export async function loadCoinDetails(coinId: string, coinData: CoinData, timeframe: number | 'max' = 30) {
  // Fetch historical data based on timeframe
  // CoinGecko API supports: 1, 7, 14, 30, 90, 180, 365, max
  let historyData: { prices: [number, number][] } = { prices: [] };
  
  // Determine interval based on timeframe for better performance
  const days = timeframe === 'max' || timeframe >= 9999 ? 'max' : timeframe;
  const interval = (typeof days === 'number' && days <= 7) ? 'hourly' : 'daily';
  
  try {
    const historyResponse = await fetch(
      `${API_BASE}/coins/${coinId}/market_chart?vs_currency=usd&days=${days}&interval=${interval}`
    );
    if (historyResponse.ok) {
      historyData = await historyResponse.json();
    }
  } catch {
    // Use fallback data
  }

  // Fetch detailed coin info (including description)
  let detailData: any = coinData;
  let description = '';
  try {
    const detailResponse = await fetch(
      `${API_BASE}/coins/${coinId}?localization=true&tickers=false&market_data=true&community_data=true&developer_data=false`
    );
    if (detailResponse.ok) {
      const fullDetailData = await detailResponse.json();
      detailData = fullDetailData;
      // Extract description (prefer English, fallback to any available)
      description = fullDetailData?.description?.en || 
                   (Object.values(fullDetailData?.description || {})[0] as string) || 
                   '';
    }
  } catch {
    // Use existing coinData
  }

  // Prepare chart data
  const prices = historyData.prices || [];
  let labels: string[] = [];
  let values: number[] = [];

  if (prices.length > 0) {
    // Format labels based on timeframe
    const daysValue = timeframe === 'max' || timeframe >= 9999 ? 9999 : (typeof timeframe === 'number' ? timeframe : 30);
    const isHourly = daysValue <= 7;
    const isLongTerm = daysValue > 90;
    
    labels = prices.map(([timestamp]) => {
      const date = new Date(timestamp);
      if (isHourly) {
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      } else if (isLongTerm) {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    });
    values = prices.map(([, price]) => price);
  } else {
    labels = ['Current'];
    values = [coinData.current_price || 0];
  }

  // Calculate predictions
  const predictions = calculatePredictions(values, coinData.current_price || 0);

  return {
    coin: coinData,
    detail: detailData,
    description,
    labels,
    values,
    predictions
  };
}

export function calculatePredictions(historicalPrices: number[], currentPrice: number): Predictions {
  if (!historicalPrices || historicalPrices.length < 7) {
    const simpleTrend =
      historicalPrices.length > 0
        ? (currentPrice - historicalPrices[0]) / historicalPrices.length
        : 0;
    return {
      day7: currentPrice + simpleTrend * 7,
      day30: currentPrice + simpleTrend * 30,
      day90: currentPrice + simpleTrend * 90,
      day7Change: (simpleTrend * 7 / currentPrice) * 100,
      day30Change: (simpleTrend * 30 / currentPrice) * 100,
      day90Change: (simpleTrend * 90 / currentPrice) * 100
    };
  }

  const recentPrices = historicalPrices.slice(-14);
  const n = recentPrices.length;

  let sumX = 0,
    sumY = 0,
    sumXY = 0,
    sumX2 = 0;

  recentPrices.forEach((price, index) => {
    const x = index;
    const y = price;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const day7Price = slope * (n + 6) + intercept;
  const day30Price = slope * (n + 29) + intercept;
  const day90Price = slope * (n + 89) + intercept;

  const day7 = Math.max(0, day7Price);
  const day30 = Math.max(0, day30Price);
  const day90 = Math.max(0, day90Price);

  return {
    day7,
    day30,
    day90,
    day7Change: ((day7 - currentPrice) / currentPrice) * 100,
    day30Change: ((day30 - currentPrice) / currentPrice) * 100,
    day90Change: ((day90 - currentPrice) / currentPrice) * 100
  };
}

function formatNumber(num: number | null | undefined): string {
  if (num == null || isNaN(num)) return '-';
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

function formatCurrency(num: number | null | undefined): string {
  if (num == null || isNaN(num)) return '-';
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
}

export { formatNumber, formatCurrency };

